// TODO: Update these test ids: https://clearcover.atlassian.net/browse/AGT-3672
/* eslint-disable testing-library/consistent-data-testid */
import React, { FC, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useDebouncedCallback } from 'use-debounce';
import { STATE_OPTIONS, StateOptionsType } from 'Shared/constants/states';
import { isPresent } from 'Shared/utils/object';
import { BaseProps, ReadOnly } from 'Shared/components/FormInput/Variants';
import { Row } from 'Shared/components/Row/Row';
import { Column } from 'Shared/components/Column/Column';
import {
  Suggestion,
  useAddressLookup,
} from 'Shared/hooks/useAddressLookup/useAddressLookup';
import { SelectInput } from '../Select';
import { FormInput, FormInputErrorMessage } from '../../FormInput';
import styles from './styles.module.css';

interface AddressType {
  [key: string]: string | any;
}
export type AddressAutocompleteProps = {
  prefix: string;
  suffixUniqueId?: string;
  values: AddressType;
  allowStateChange?: boolean;
  errorMessages: {
    address1: string | undefined;
    address2: string | undefined;
    city: string | undefined;
    state: string | undefined;
    zip: string | undefined;
  };
  onAddressLine1Change: (value: string) => void;
  onAddressLine2Change: (value: string) => void;
  onCityChange: (value: string) => void;
  onStateChange: (value: string) => void;
  onZipChange: (value: string) => void;
  onAddressSelect: (value: Suggestion) => void;
  fieldNames?: {
    address1: string;
    address2: string;
    city: string;
    state: string;
    zip: string;
  };
  stateOptions?: StateOptionsType;
  readOnly?: boolean;
  tooltip?: string;
} & Omit<BaseProps, 'options' | 'label'>;

/*
  TODO: Refactor this component to be much more customizable for the consuming component.
  This component's responsibilities should only be to deal with address lookup and provide
  those options to the consuming component.  The consuming component can setup its own form
  fields however it wants.
*/
export const AddressAutocomplete: FC<AddressAutocompleteProps> = ({
  prefix,
  allowStateChange = true,
  values,
  errorMessages,
  disabled,
  onAddressSelect,
  onAddressLine1Change,
  onAddressLine2Change,
  onCityChange,
  onStateChange,
  onZipChange,
  onBlur,
  fieldNames,
  stateOptions = STATE_OPTIONS,
  readOnly,
  tooltip,
}) => {
  const [{ data, loading }, getAddressSuggestions] = useAddressLookup({
    stateFilter: Object.keys(stateOptions),
  });
  const [addressOptions, setAddressOptions] = useState<Suggestion[]>([]);

  const getDebouncedSuggestions = useDebouncedCallback((address) => {
    if (address.length > 0) {
      getAddressSuggestions(address);
    }
  }, 200);

  useEffect(() => {
    if (data) setAddressOptions(data);
  }, [data]);

  const options = useMemo(() => {
    if (!allowStateChange) {
      return addressOptions
        .filter((option) => option.state === values.state)
        .reduce((acc, option) => {
          return { ...acc, [option.text]: option.text };
        }, {});
    } else {
      return addressOptions.reduce((acc, option) => {
        return { ...acc, [option.text]: option.text };
      }, {});
    }
  }, [addressOptions, allowStateChange, values]);
  return (
    <>
      <Row>
        <Column
          variant="threeFourths"
          className={classNames('fs-exclude', styles.addressColumn)}
        >
          {readOnly && (
            <ReadOnly
              name={
                fieldNames?.address1
                  ? fieldNames.address1
                  : `${prefix}.address1`
              }
              options={options}
              value={values.address1}
              label="Street Address"
              tooltip={
                tooltip ??
                'This address should be where you reside and generally receive mail that isn’t a P.O. box or your office.'
              }
              tooltipPosition="bottom"
            />
          )}
          {!readOnly && (
            <SelectInput
              variant="typeahead"
              name={
                fieldNames?.address1
                  ? fieldNames.address1
                  : `${prefix}.address1`
              }
              className={classNames({
                [styles.hasError]: isPresent(errorMessages.address1),
              })}
              data-testid="address.address1"
              loading={loading}
              options={options}
              onChange={(address) => {
                // downshift work arounds to avoid setting the same value or briefly setting the key on selection
                if (
                  address !== values.address1 &&
                  !Object.keys(options).includes(address)
                ) {
                  onAddressLine1Change(address);
                  getDebouncedSuggestions(address);
                }
              }}
              onSelect={(selectedAddress: string) => {
                const selectedOption = addressOptions.find((address) =>
                  address.text.includes(selectedAddress)
                );

                if (selectedOption) {
                  onAddressSelect(selectedOption);
                }
              }}
              onBlur={onBlur}
              value={values.address1}
              label="Street Address"
              tooltip={
                tooltip ??
                'This address should be where you reside and generally receive mail that isn’t a P.O. box or your office.'
              }
              tooltipPosition="bottom"
              disabled={disabled}
            />
          )}
          {errorMessages.address1 && (
            <FormInputErrorMessage message={errorMessages.address1} />
          )}
        </Column>
        <Column variant="quarter" className="fs-exclude">
          <FormInput
            variant={readOnly ? 'readOnly' : 'text'}
            name={
              fieldNames?.address2 ? fieldNames.address2 : `${prefix}.address2`
            }
            data-testid="address.address2"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onAddressLine2Change(e.target.value)
            }
            onBlur={onBlur}
            value={values.address2}
            errorMessage={errorMessages.address2}
            label="Apt / Suite #"
            disabled={disabled}
          />
        </Column>
      </Row>
      <Row>
        <Column className="fs-exclude">
          <FormInput
            variant={readOnly ? 'readOnly' : 'text'}
            name={fieldNames?.city ? fieldNames.city : `${prefix}.city`}
            data-testid="address.city"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onCityChange(e.target.value)
            }
            onBlur={onBlur}
            value={values.city}
            errorMessage={errorMessages.city}
            label="City"
            disabled={disabled}
          />
        </Column>
        <Column className="fs-exclude">
          <FormInput
            variant={readOnly ? 'readOnly' : 'selectAutocomplete'}
            name={fieldNames?.state ? fieldNames.state : `${prefix}.state`}
            data-testid="edit-detail-state"
            onChange={onStateChange}
            onBlur={onBlur}
            value={values.state}
            errorMessage={errorMessages.state}
            label="State"
            options={stateOptions}
            disabled={!allowStateChange || disabled}
          />
        </Column>
        <Column className="fs-exclude">
          <FormInput
            variant={readOnly ? 'readOnly' : 'text'}
            name={fieldNames?.zip ? fieldNames.zip : `${prefix}.zip`}
            data-testid="address.zip"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onZipChange(e.target.value)
            }
            onBlur={onBlur}
            value={values.zip}
            errorMessage={errorMessages.zip}
            label="ZIP Code"
            disabled={disabled}
          />
        </Column>
      </Row>
    </>
  );
};
